import { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  AdminGetProjectResponse,
  AdminProjectOverviewResponse,
  ReviewAsset,
  ReviewEvent,
} from '../../shared/types';
import Chip from '../components/Chip';
import Eyebrow from '../components/Eyebrow';
import StepFlow from '../components/StepFlow';
import { showToast } from '../components/Toaster';
import AdminProjectMonitoring from '../components/admin/AdminProjectMonitoring';
import AdminTranscriptImport from '../components/admin/AdminTranscriptImport';
import CopyButton from '../components/admin/CopyButton';
import ExportPanel from '../components/admin/ExportPanel';
import RowMenu from '../components/admin/RowMenu';
import ShareLinksDisclosure, { nextShareLinksExpanded } from '../components/admin/ShareLinksDisclosure';
import { AssetUploader } from '../components/upload/AssetUploader';
import {
  AdminFooter,
  AdminShell,
  FieldLabel,
  SectionHeading,
  StatePanel,
  StatusChip,
  UploadChip,
  cardClass,
  expirySummary,
  formatBytes,
  formatDate,
  formatDateTime,
  formatDuration,
  inputClass,
  primaryButtonGlowClass,
  quietButtonClass,
  quietSmButtonClass,
  secondaryButtonClass,
  secondarySmButtonClass,
} from '../components/admin/AdminUi';
import { adminApi, ApiError } from '../lib/api';

export default function AdminProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<AdminGetProjectResponse | null>(null);
  const [overview, setOverview] = useState<AdminProjectOverviewResponse | null>(null);
  const [events, setEvents] = useState<ReviewEvent[]>([]);
  const [nextActivityCursor, setNextActivityCursor] = useState<string | null>(null);
  const [activityExpanded, setActivityExpanded] = useState(false);
  const [activityBusy, setActivityBusy] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);
  const activityLoadId = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importedAssetIds, setImportedAssetIds] = useState<Set<string>>(() => new Set());

  const load = useCallback(async () => {
    if (!id) return;
    activityLoadId.current += 1;
    setActivityExpanded(false);
    setActivityBusy(true);
    setActivityError(null);
    setError(null);
    try {
      const [nextDetail, nextOverview, activity] = await Promise.all([
        adminApi.getProject(id),
        adminApi.overview(id),
        adminApi.activity({ project_id: id, limit: 5 }),
      ]);
      setDetail(nextDetail);
      setImportedAssetIds((current) =>
        new Set([...current, ...nextDetail.transcript_imported_asset_ids]),
      );
      setOverview(nextOverview);
      setEvents(activity.events);
      setNextActivityCursor(activity.next_cursor);
      setActivityBusy(false);
    } catch (caught) {
      setActivityBusy(false);
      setError(caught instanceof ApiError ? caught.message : 'We could not load that project. Mind refreshing?');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const showAllActivity = useCallback(async () => {
    setActivityExpanded(true);
    if (!id || !nextActivityCursor) return;

    const loadId = activityLoadId.current;
    setActivityBusy(true);
    setActivityError(null);
    try {
      let cursor: string | null = nextActivityCursor;
      const olderEvents: ReviewEvent[] = [];
      while (cursor) {
        const activity = await adminApi.activity({ project_id: id, cursor, limit: 50 });
        olderEvents.push(...activity.events);
        cursor = activity.next_cursor;
      }
      if (loadId !== activityLoadId.current) return;
      setEvents((current) => [...current, ...olderEvents]);
      setNextActivityCursor(null);
    } catch (caught) {
      if (loadId !== activityLoadId.current) return;
      setActivityExpanded(false);
      setActivityError(caught instanceof ApiError ? caught.message : 'We could not load all activity. Mind trying again?');
    } finally {
      if (loadId === activityLoadId.current) setActivityBusy(false);
    }
  }, [id, nextActivityCursor]);

  if (!id) return null;

  return (
    <AdminShell>
      <div className="mx-auto max-w-6xl px-5 py-8 md:px-10 md:py-12">
        {error ? (
          <StatePanel eyebrow="Could not load" title="We could not open this project" tone="error">
            <p>{error}</p>
            <button type="button" className={`${secondaryButtonClass} mt-5`} onClick={() => void load()}>Try again</button>
          </StatePanel>
        ) : !detail || !overview ? (
          <StatePanel eyebrow="Loading" title="Opening the project"><p>We are gathering assets, links, and review activity now.</p></StatePanel>
        ) : (
          <>
            <ProjectHeader detail={detail} onChanged={load} />
            {importOpen ? (
              <div className="mt-8">
                <AdminTranscriptImport
                  projectId={id}
                  assets={detail.assets}
                  onClose={() => setImportOpen(false)}
                  onImported={(assetIds) => {
                    setImportedAssetIds((current) => new Set([...current, ...assetIds]));
                    void load();
                  }}
                />
              </div>
            ) : (
              <div className="mt-8 grid gap-6">
                <PublishSection projectId={id} detail={detail} importedAssetIds={importedAssetIds} onChanged={load} />
                <AssetsSection
                  projectId={id}
                  assets={detail.assets}
                  importedAssetIds={importedAssetIds}
                  onChanged={load}
                  onImport={() => setImportOpen(true)}
                />
                <MonitoringSection
                  overview={overview}
                  events={events}
                  nextActivityCursor={nextActivityCursor}
                  activityExpanded={activityExpanded}
                  activityBusy={activityBusy}
                  activityError={activityError}
                  onShowAllActivity={showAllActivity}
                  onCollapseActivity={() => setActivityExpanded(false)}
                />
                <ExportPanel projectId={id} projectTitle={detail.project.title} reviewers={overview.reviewers} />
                <DangerSection
                  projectId={id}
                  title={detail.project.title}
                  completed={detail.project.status === 'completed'}
                  onCompleted={load}
                  onDeleted={() => navigate('/admin', { replace: true })}
                />
              </div>
            )}
          </>
        )}
      </div>
      <AdminFooter path={`ss-review / project / ${id}`} />
    </AdminShell>
  );
}

function ProjectHeader({ detail, onChanged }: { detail: AdminGetProjectResponse; onChanged: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(detail.project.title);
  const [clientName, setClientName] = useState(detail.project.client_name);
  const [instructions, setInstructions] = useState(detail.project.instructions ?? '');
  const [allowTourSkip, setAllowTourSkip] = useState(detail.project.allowTourSkip);
  const [busy, setBusy] = useState(false);
  const [extending, setExtending] = useState<30 | 60 | 'indefinite' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitle(detail.project.title);
    setClientName(detail.project.client_name);
    setInstructions(detail.project.instructions ?? '');
    setAllowTourSkip(detail.project.allowTourSkip);
  }, [detail.project]);

  // A project kept indefinitely has no date and no countdown to report, so it
  // collapses to one segment rather than reading "expires no expiry · indefinite".
  const expiryMeta = detail.project.expiry_at === null
    ? ['No expiry']
    : [`expires ${formatDate(detail.project.expiry_at)}`, expirySummary(detail.project.expiry_at)];

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminApi.updateProject(detail.project.id, {
        title,
        client_name: clientName,
        instructions: instructions.trim() || null,
        allowTourSkip,
      });
      await onChanged();
      setEditing(false);
      showToast('success', 'Details saved.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not save those details. Mind trying again?');
    } finally {
      setBusy(false);
    }
  }

  async function extend(extensionDays: 30 | 60 | null) {
    setBusy(true);
    setExtending(extensionDays ?? 'indefinite');
    setError(null);
    try {
      await adminApi.extendProject(detail.project.id, { extension_days: extensionDays });
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not update the expiry. Mind trying again?');
    } finally {
      setBusy(false);
      setExtending(null);
    }
  }

  return (
    <header className="border-b border-line pb-10">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <div className="rise">
          <SectionHeading
            level="page"
            eyebrow={(
              <div className="flex flex-wrap items-center gap-3">
                <Eyebrow accent>Project</Eyebrow>
                <StatusChip status={detail.project.status} />
              </div>
            )}
            title={detail.project.title}
          />
          <p className="eyebrow mt-2">
            {[detail.project.client_name, ...expiryMeta].join(' · ')}
          </p>
        </div>
        <div className="rise flex flex-wrap justify-end gap-2" style={{ animationDelay: '260ms' }}>
          <button type="button" className={secondarySmButtonClass} onClick={() => setEditing((value) => !value)}>
            {editing ? 'Close details' : 'Edit details'}
          </button>
          <RowMenu
            items={[
              { label: 'Add 30 days', onClick: () => void extend(30), disabled: busy },
              { label: 'Add 60 days', onClick: () => void extend(60), disabled: busy },
              { label: 'Keep indefinitely', onClick: () => void extend(null), disabled: busy },
            ]}
            renderTrigger={(triggerProps) => (
              <button {...triggerProps} className={secondarySmButtonClass} disabled={busy}>
                {extending !== null ? 'Updating expiry' : (
                  <>
                    Extend expiry
                    <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path d="m3 4.5 3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
            )}
          />
        </div>
      </div>

      {detail.project.instructions ? (
        <div className="rise-subtle mt-6 max-w-3xl rounded-card border border-line bg-card p-5 shadow-card" style={{ animationDelay: '320ms' }}>
          <Eyebrow>Reviewer instructions</Eyebrow>
          <p className="mt-3 whitespace-pre-wrap text-sm text-muted">{detail.project.instructions}</p>
        </div>
      ) : null}

      {editing ? (
        <form className={`${cardClass} mt-6 grid gap-4 p-6 md:grid-cols-2`} onSubmit={save}>
          <label>
            <FieldLabel>Project title</FieldLabel>
            <input className={inputClass} value={title} onChange={(event) => setTitle(event.target.value)} required />
          </label>
          <label>
            <FieldLabel>Client name</FieldLabel>
            <input className={inputClass} value={clientName} onChange={(event) => setClientName(event.target.value)} required />
          </label>
          <label className="md:col-span-2">
            <FieldLabel>Reviewer instructions</FieldLabel>
            <textarea className={`${inputClass} min-h-28 resize-y`} value={instructions} onChange={(event) => setInstructions(event.target.value)} />
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-input border border-line bg-paper-200 px-4 py-3.5 md:col-span-2">
            <input
              type="checkbox"
              className="mt-0.5 h-[17px] w-[17px] shrink-0 accent-accent"
              checked={allowTourSkip}
              onChange={(event) => setAllowTourSkip(event.target.checked)}
            />
            <span>
              <span className="block text-sm font-semibold text-fg">Allow skipping the tour</span>
              <span className="mt-px block text-[12.5px] text-muted">Adds a Skip link to the first-visit tour. Useful when a review has multiple stakeholders.</span>
            </span>
          </label>
          {error ? <p className="rounded-input bg-error-tint p-3 text-sm text-error md:col-span-2">{error}</p> : null}
          <div className="md:col-span-2">
            <button type="submit" className={secondaryButtonClass} disabled={busy}>{busy ? <><span className="inline-spinner" aria-hidden="true" />Saving details</> : 'Save details'}</button>
          </div>
        </form>
      ) : error ? <p className="mt-4 rounded-input bg-error-tint p-3 text-sm text-error">{error}</p> : null}
    </header>
  );
}

function AssetsSection({
  projectId,
  assets,
  importedAssetIds,
  onChanged,
  onImport,
}: {
  projectId: string;
  assets: ReviewAsset[];
  importedAssetIds: Set<string>;
  onChanged: () => Promise<void>;
  onImport: () => void;
}) {
  const assetsMissingMedia = assets.filter((asset) => asset.upload_state !== 'uploaded');
  const [showAdd, setShowAdd] = useState(false);
  const [uploadAsset, setUploadAsset] = useState<ReviewAsset | null>(null);
  const [batchUploadOpen, setBatchUploadOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function move(index: number, direction: -1 | 1) {
    const next = [...assets];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setBusy(true);
    try {
      await adminApi.reorderAssets(projectId, { asset_ids: next.map((asset) => asset.id) });
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not change the asset order. Mind trying again?');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={`${cardClass} rise-subtle p-6 md:p-8`} style={{ animationDelay: '380ms' }} aria-labelledby="assets-title">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <SectionHeading
          eyebrow={<Eyebrow accent>Assets</Eyebrow>}
          title={<span id="assets-title">Interviews and transcripts</span>}
        />
        <div className="flex flex-wrap gap-2">
          <button type="button" className={secondaryButtonClass} onClick={onImport}>Import transcript</button>
          {assetsMissingMedia.length > 1 ? (
            <button
              type="button"
              className={secondaryButtonClass}
              onClick={() => setBatchUploadOpen((value) => !value)}
            >
              Upload videos
            </button>
          ) : null}
          <button type="button" className={secondaryButtonClass} onClick={() => setShowAdd((value) => !value)}>{showAdd ? 'Close asset form' : 'Add asset'}</button>
        </div>
      </div>
      {showAdd ? <AddAssetForm projectId={projectId} onAdded={async () => { setShowAdd(false); await onChanged(); }} /> : null}
      {error ? <p className="mt-6 rounded-input bg-error-tint p-3 text-sm text-error">{error}</p> : null}
      {assets.length === 0 ? (
        <div className="mt-6 rounded-card border border-dashed border-line bg-page p-8 text-center">
          <h3 className="text-2xl">No interviews yet</h3>
          <p className="mt-2 text-sm text-muted">Import a transcript to propose one asset for each section. If you do not have a transcript yet, you can still add assets manually.</p>
        </div>
      ) : (
        <ol className="mt-6 divide-y divide-line border-y border-line">
          {assets.map((asset, index) => (
            <AssetRow
              key={asset.id}
              asset={asset}
              index={index}
              count={assets.length}
              imported={importedAssetIds.has(asset.id)}
              busy={busy}
              onMove={(direction) => void move(index, direction)}
              onChanged={onChanged}
              onUpload={() => setUploadAsset(asset)}
            />
          ))}
        </ol>
      )}
      {batchUploadOpen ? (
        <div className="mt-6">
          <AssetUploader
            asset={assetsMissingMedia}
            onComplete={() => void onChanged()}
            onQueueComplete={() => setBatchUploadOpen(false)}
          />
          <button
            type="button"
            className={`${quietButtonClass} mt-3`}
            onClick={() => setBatchUploadOpen(false)}
          >
            Close
          </button>
        </div>
      ) : null}
      {uploadAsset ? (
        <div className="mt-6">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
            <AssetUploader asset={uploadAsset} onComplete={() => void onChanged()} />
            <PosterUploader asset={uploadAsset} onChanged={onChanged} />
          </div>
          <button type="button" className={`${quietButtonClass} mt-3`} onClick={() => setUploadAsset(null)}>Close upload slot</button>
        </div>
      ) : null}
    </section>
  );
}

function PosterUploader({ asset, onChanged }: { asset: ReviewAsset; onChanged: () => Promise<void> }) {
  const [status, setStatus] = useState<'idle' | 'uploading' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function uploadPoster(event: ChangeEvent<HTMLInputElement>) {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;
    if (file.type !== 'image/jpeg' && file.type !== 'image/webp') {
      setError('Choose a JPEG or WebP poster image.');
      input.value = '';
      return;
    }

    setStatus('uploading');
    setError(null);
    try {
      const prepared = await adminApi.createUploadUrl(asset.id, {
        filename: file.name,
        mime_type: file.type,
        bytes: file.size,
      });
      const response = await fetch(prepared.upload_url, {
        method: 'PUT',
        headers: prepared.required_headers,
        body: file,
      });
      if (!response.ok) throw new Error('Storage did not accept the poster. Please try again.');
      await adminApi.completeUpload(asset.id, {
        r2_key: prepared.r2_key,
        bytes: file.size,
        etag: response.headers.get('ETag') ?? '',
      });
      await onChanged();
      setStatus('done');
    } catch (caught) {
      setStatus('idle');
      setError(caught instanceof Error ? caught.message : 'We could not upload that poster. Mind trying again?');
    } finally {
      input.value = '';
    }
  }

  const uploading = status === 'uploading';

  return (
    <section className="rounded-card border border-line bg-elevated p-4 lg:w-64" aria-label="Poster upload">
      <FieldLabel>Poster image</FieldLabel>
      <p className="mt-2 text-sm text-muted">JPEG or WebP.</p>
      <label className={`${quietButtonClass} poster-upload-control mt-3 gap-2 ${uploading ? 'pointer-events-none opacity-55' : 'cursor-pointer'}`} aria-disabled={uploading}>
        <input className="sr-only" type="file" accept="image/jpeg,image/webp" disabled={uploading} onChange={(event) => void uploadPoster(event)} />
        {uploading ? <span className="inline-spinner" aria-hidden="true" /> : null}
        {uploading ? 'Uploading poster' : 'Upload poster'}
      </label>
      {status === 'done' ? <p className="mt-3 text-sm font-semibold text-success" role="status">Poster uploaded.</p> : null}
      {error ? <p className="mt-3 text-sm text-error" role="alert">{error}</p> : null}
    </section>
  );
}

function AddAssetForm({ projectId, onAdded }: { projectId: string; onAdded: () => Promise<void> }) {
  const [title, setTitle] = useState('');
  const [speaker, setSpeaker] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminApi.createAsset(projectId, { title, speaker_label: speaker.trim() || null });
      await onAdded();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not add that asset. Mind trying again?');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="mt-6 grid gap-4 rounded-card bg-elevated p-5 sm:grid-cols-2" onSubmit={submit}>
      <label>
        <FieldLabel>Asset title</FieldLabel>
        <input className={inputClass} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Interview with Alex" required />
      </label>
      <label>
        <FieldLabel>Speaker label</FieldLabel>
        <input className={inputClass} value={speaker} onChange={(event) => setSpeaker(event.target.value)} placeholder="Alex" />
      </label>
      {error ? <p className="rounded-input bg-error-tint p-3 text-sm text-error sm:col-span-2">{error}</p> : null}
      <div className="sm:col-span-2"><button type="submit" className={secondaryButtonClass} disabled={busy}>{busy ? <><span className="inline-spinner" aria-hidden="true" />Adding asset</> : 'Add asset'}</button></div>
    </form>
  );
}

function AssetRow({
  asset,
  index,
  count,
  imported,
  busy,
  onMove,
  onChanged,
  onUpload,
}: {
  asset: ReviewAsset;
  index: number;
  count: number;
  imported: boolean;
  busy: boolean;
  onMove: (direction: -1 | 1) => void;
  onChanged: () => Promise<void>;
  onUpload: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(asset.title);
  const [speaker, setSpeaker] = useState(asset.speaker_label ?? '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmMediaDelete, setConfirmMediaDelete] = useState(false);
  const [rowBusyAction, setRowBusyAction] = useState<'save' | 'delete-media' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setRowBusyAction('save');
    setError(null);
    try {
      await adminApi.updateAsset(asset.id, { title, speaker_label: speaker.trim() || null });
      await onChanged();
      setEditing(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not save that asset. Mind trying again?');
    } finally {
      setRowBusyAction(null);
    }
  }

  async function remove() {
    setRowBusyAction('delete');
    setError(null);
    try {
      await adminApi.deleteAsset(asset.id);
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not delete that asset. Mind trying again?');
    } finally {
      setRowBusyAction(null);
    }
  }

  async function removeMedia() {
    setRowBusyAction('delete-media');
    setError(null);
    try {
      await adminApi.deleteAssetMedia(asset.id);
      await onChanged();
      setConfirmMediaDelete(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not delete that video. Mind trying again?');
    } finally {
      setRowBusyAction(null);
    }
  }

  return (
    <li className="group relative py-5">
      <div className="grid gap-4 lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:items-center">
        <div className="flex w-[26px] flex-col">
          <button
            type="button"
            className="inline-flex h-[22px] w-[26px] items-center justify-center rounded-input text-faint transition-colors hover:bg-elevated hover:text-fg disabled:cursor-not-allowed disabled:opacity-35"
            onClick={() => onMove(-1)}
            disabled={busy || index === 0}
            aria-label={`Move ${asset.title} up`}
          >
            <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="m3 7.5 3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            className="inline-flex h-[22px] w-[26px] items-center justify-center rounded-input text-faint transition-colors hover:bg-elevated hover:text-fg disabled:cursor-not-allowed disabled:opacity-35"
            onClick={() => onMove(1)}
            disabled={busy || index === count - 1}
            aria-label={`Move ${asset.title} down`}
          >
            <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="m3 4.5 3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="font-display text-[1.05rem] font-semibold">{asset.title}</h3>
            <UploadChip state={asset.upload_state} />
            <Chip tone={imported ? 'teal' : 'warning'}>{imported ? 'Transcript' : 'No transcript yet'}</Chip>
          </div>
          <p className="eyebrow mt-2">
            {asset.speaker_label || 'Speaker not set'} · {formatDuration(asset.duration_s)} · {formatBytes(asset.bytes)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {asset.upload_state !== 'uploaded' ? (
            <button type="button" className={secondarySmButtonClass} onClick={onUpload}>Upload media</button>
          ) : null}
          <RowMenu
            label={`Actions for ${asset.title}`}
            items={[
              { label: 'Upload media', onClick: onUpload },
              { label: 'Edit', onClick: () => setEditing((value) => !value) },
              ...(asset.r2_key === null ? [] : [{
                label: 'Delete video only',
                onClick: () => {
                  setConfirmMediaDelete(true);
                  setConfirmDelete(false);
                },
                destructive: true,
              }]),
              {
                label: 'Delete interview',
                onClick: () => {
                  setConfirmDelete(true);
                  setConfirmMediaDelete(false);
                },
                destructive: true,
              },
            ]}
          />
        </div>
      </div>
      {editing ? (
        <div className="mt-4 grid gap-4 rounded-input bg-elevated p-4 sm:grid-cols-2">
          <label><FieldLabel>Title</FieldLabel><input className={inputClass} value={title} onChange={(event) => setTitle(event.target.value)} /></label>
          <label><FieldLabel>Speaker</FieldLabel><input className={inputClass} value={speaker} onChange={(event) => setSpeaker(event.target.value)} /></label>
          <div className="sm:col-span-2"><button type="button" className={secondaryButtonClass} onClick={() => void save()} disabled={rowBusyAction !== null}>{rowBusyAction === 'save' ? <><span className="inline-spinner" aria-hidden="true" />Saving asset</> : 'Save asset'}</button></div>
        </div>
      ) : null}
      {confirmMediaDelete ? (
        <div className="mt-4 rounded-input bg-warning-tint p-4 text-sm text-warning">
          <p>Delete only the video for {asset.title}? The transcript and every decision and annotation on this asset will stay. You can upload a new video afterwards.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className={quietButtonClass} onClick={() => setConfirmMediaDelete(false)}>Keep video</button>
            <button type="button" className={quietButtonClass} onClick={() => void removeMedia()} disabled={rowBusyAction !== null}>{rowBusyAction === 'delete-media' ? 'Deleting video' : 'Confirm video deletion'}</button>
          </div>
        </div>
      ) : null}
      {confirmDelete ? (
        <div className="mt-4 rounded-input bg-error-tint p-4 text-sm text-error">
          <p>Delete {asset.title} entirely? This removes its video, transcript and every decision on it. This cannot be undone.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className={quietButtonClass} onClick={() => setConfirmDelete(false)}>Keep asset</button>
            <button type="button" className={quietButtonClass} onClick={() => void remove()} disabled={rowBusyAction !== null}>{rowBusyAction === 'delete' ? 'Deleting' : 'Confirm delete'}</button>
          </div>
        </div>
      ) : null}
      {error ? <p className="mt-3 rounded-input bg-error-tint p-3 text-sm text-error">{error}</p> : null}
    </li>
  );
}

/** Busy key for the grant block, which can revoke several links in one go. */
const EDIT_ACCESS_BUSY = 'edit-access';

function PublishSection({ projectId, detail, importedAssetIds, onChanged }: { projectId: string; detail: AdminGetProjectResponse; importedAssetIds: Set<string>; onChanged: () => Promise<void> }) {
  const [usePin, setUsePin] = useState(false);
  const [pin, setPin] = useState('');
  const [oneTimeUrl, setOneTimeUrl] = useState<string | null>(null);
  const [oneTimeKind, setOneTimeKind] = useState<'client' | 'edit'>('client');
  const [wasReissued, setWasReissued] = useState(false);
  const [oneTimeEditLink, setOneTimeEditLink] = useState<{ id: string; url: string } | null>(null);
  const [shareLinksExpanded, setShareLinksExpanded] = useState(false);
  const [busyAction, setBusyAction] = useState<'create' | string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const oneTimeField = useRef<HTMLInputElement>(null);

  async function createLink(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyAction('create');
    setError(null);
    try {
      const response = await adminApi.createShareLink(projectId, {
        mode: usePin ? 'pin' : 'open',
        pin: usePin ? pin : undefined,
      });
      setOneTimeUrl(response.share_url);
      setOneTimeKind('client');
      setWasReissued(false);
      setPin('');
      setShareLinksExpanded((current) => nextShareLinksExpanded(current, 'link-created'));
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not create the share link. Mind trying again?');
    } finally {
      setBusyAction(null);
    }
  }

  /**
   * Created when Henry reaches for the copy button, not on page load, so a
   * project still only carries the grant once he goes to send it. CopyButton
   * runs this on hover or focus rather than mid-click, because awaiting it
   * inside the click is what broke the clipboard on Safari and in the iframe.
   */
  async function resolveEditAccessUrl(): Promise<string | null> {
    if (editAccessUrl) return editAccessUrl;
    setError(null);
    try {
      const response = await adminApi.createEditAccessLink(projectId);
      setOneTimeEditLink({ id: response.share_link.id, url: response.share_url });
      // The refresh runs alongside the copy, never before it: awaiting a whole
      // project refetch here would spend the click the clipboard depends on.
      void onChanged().catch(() => setError('The link is copied, but this page did not refresh. Reload to see it listed.'));
      return response.share_url;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not create the edit access link. Mind trying again?');
      return null;
    }
  }

  async function reissue(linkId: string) {
    setBusyAction(`reissue:${linkId}`);
    setError(null);
    try {
      const response = await adminApi.reissueShareLink(linkId);
      const isEdit = response.share_link.can_edit_all === 1;
      setOneTimeUrl(response.share_url);
      setOneTimeKind(isEdit ? 'edit' : 'client');
      setWasReissued(true);
      if (isEdit) setOneTimeEditLink({ id: linkId, url: response.share_url });
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not reissue that link. Mind trying again?');
    } finally {
      setBusyAction(null);
    }
  }

  async function revoke(linkId: string) {
    setBusyAction(linkId);
    setError(null);
    try {
      await adminApi.revokeShareLink(linkId);
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not revoke that link. Mind trying again?');
    } finally {
      setBusyAction(null);
    }
  }

  /** Every live grant goes at once: a grant left behind is a grant Henry cannot see. */
  async function revokeEditAccess() {
    setBusyAction(EDIT_ACCESS_BUSY);
    setError(null);
    try {
      for (const link of editAccessLinks) await adminApi.revokeShareLink(link.id);
      setOneTimeEditLink(null);
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not revoke that link. Mind trying again?');
    } finally {
      setBusyAction(null);
    }
  }

  const transcriptReady = detail.assets.length > 0 && detail.assets.every((asset) => importedAssetIds.has(asset.id));
  const mediaReady = detail.assets.length > 0 && detail.assets.every((asset) => asset.upload_state === 'uploaded');
  // The edit access link lives in its own block, so it never reads as a second client link.
  const clientLinks = detail.share_links.filter((link) => link.can_edit_all === 0);
  // Every unrevoked grant, not just the newest: a second one would otherwise
  // stay live and invisible behind the link this block shows.
  const editAccessLinks = detail.share_links.filter((link) => link.can_edit_all === 1 && link.revoked_at === null);
  const editAccessLink = editAccessLinks[0] ?? null;
  const editAccessUrl = oneTimeEditLink && (!editAccessLink || oneTimeEditLink.id === editAccessLink.id)
    ? oneTimeEditLink.url : editAccessLink?.share_url;
  const shareReady = clientLinks.some((link) => link.revoked_at === null);

  return (
    <section className="card-accent rise-subtle p-6 md:p-8" style={{ animationDelay: '320ms' }} aria-labelledby="publish-title">
      <Eyebrow accent>Publish</Eyebrow>
      <h2 id="publish-title" className="sr-only">Client access</h2>
      <StepFlow
        className="mt-5"
        steps={[
          {
            title: 'Import the transcript',
            detail: transcriptReady ? `${detail.assets.length} ${detail.assets.length === 1 ? 'interview' : 'interviews'} ready` : 'Import a transcript for each interview.',
            state: transcriptReady ? 'done' : 'current',
            tone: 'gold',
          },
          {
            title: 'Upload the media',
            detail: mediaReady ? 'Every interview proxy is uploaded.' : 'Upload a proxy for each interview.',
            state: mediaReady ? 'done' : transcriptReady ? 'current' : 'pending',
            tone: 'gold',
          },
          {
            title: `Share with ${detail.project.client_name}`,
            detail: shareReady ? 'The private client link is active.' : 'Create a private link, with a PIN if the footage needs it.',
            state: shareReady ? 'done' : mediaReady ? 'current' : 'pending',
            tone: 'gold',
          },
        ]}
      />

      {oneTimeUrl ? (
        <div className="mt-6 rounded-card border border-accent/30 bg-accent-tint p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-accent">Ready to share</p>
          <h3 className="mt-2 text-2xl">Your {oneTimeKind === 'edit' ? 'edit access' : 'client'} link is ready</h3>
          {wasReissued ? <p className="mt-2 text-sm text-muted">Replaces the previous link. Open reviews keep working.</p> : null}
          <input ref={oneTimeField} className={inputClass} value={oneTimeUrl} readOnly aria-label="New share link" />
          <div className="mt-4 flex flex-wrap gap-2">
            <CopyButton
              text={oneTimeUrl}
              className={secondaryButtonClass}
              successMessage="Link copied."
              onManualFallback={() => oneTimeField.current?.select()}
            />
            <button type="button" className={quietButtonClass} onClick={() => setOneTimeUrl(null)}>I have saved it</button>
          </div>
          <p className="mt-3 text-xs text-muted">Save this link now. For security, it cannot be retrieved after you leave this page.</p>
        </div>
      ) : null}

      <form className="mt-6 grid gap-4 border-t border-line pt-6 sm:grid-cols-[1fr_auto] sm:items-end" onSubmit={createLink}>
        <div>
          <label className="flex min-h-11 items-center gap-3 text-sm text-muted">
            <input type="checkbox" className="h-5 w-5 accent-accent" checked={usePin} onChange={(event) => setUsePin(event.target.checked)} />
            Require a PIN
          </label>
          {usePin ? (
            <label className="mt-3 block">
              <FieldLabel>Client PIN</FieldLabel>
              <input className={inputClass} type="password" value={pin} onChange={(event) => setPin(event.target.value)} minLength={4} required />
            </label>
          ) : null}
        </div>
        <button type="submit" className={primaryButtonGlowClass} disabled={busyAction !== null || Boolean(oneTimeUrl)}>{busyAction === 'create' ? <><span className="inline-spinner" aria-hidden="true" />Creating link</> : 'Create share link'}</button>
      </form>
      {error ? <p className="mt-4 rounded-input bg-error-tint p-3 text-sm text-error">{error}</p> : null}

      <div className="mt-7">
        <ShareLinksDisclosure
          links={clientLinks}
          expanded={shareLinksExpanded}
          busyAction={busyAction}
          onToggle={() => setShareLinksExpanded((current) => nextShareLinksExpanded(current, 'toggle'))}
          onRevoke={(linkId) => void revoke(linkId)}
          onReissue={(linkId) => void reissue(linkId)}
        />
      </div>

      {/* Offered once the client has a link of their own: this one is for the lead contact, not for publishing. */}
      {shareReady || editAccessLink ? (
        <div className="mt-7 border-t border-line pt-6">
          <FieldLabel>Edit access link</FieldLabel>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
            <p className="max-w-md text-sm text-muted">
              For your lead contact — whoever opens the review through this link can adjust <b className="font-semibold text-fg">everyone’s</b> selects.
              {editAccessLink && !editAccessUrl ? <> Replaces the previous link. Open reviews keep working.</> : null}
              {editAccessLinks.length > 1 ? <> <b className="font-semibold text-warning">{editAccessLinks.length} of these are live.</b> Revoking clears them all.</> : null}
            </p>
            <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
              {editAccessLink && !editAccessUrl ? (
                <button type="button" className={quietSmButtonClass}
                  onClick={() => void reissue(editAccessLink.id)} disabled={busyAction !== null}>
                  {busyAction === `reissue:${editAccessLink.id}` ? 'Reissuing' : 'Reissue link'}
                </button>
              ) : <CopyButton
                text={editAccessUrl}
                onResolveText={resolveEditAccessUrl}
                busyLabel="Creating link"
                manualLabel="Edit access link to copy manually"
                successMessage="Edit access link copied."
                disabled={busyAction !== null}
              />}
              {editAccessLink ? (
                <button
                  type="button"
                  className={quietSmButtonClass}
                  onClick={() => void revokeEditAccess()}
                  disabled={busyAction !== null}
                >
                  {busyAction === EDIT_ACCESS_BUSY ? 'Revoking' : editAccessLinks.length > 1 ? `Revoke all ${editAccessLinks.length}` : 'Revoke'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function MonitoringSection({
  overview,
  events,
  nextActivityCursor,
  activityExpanded,
  activityBusy,
  activityError,
  onShowAllActivity,
  onCollapseActivity,
}: {
  overview: AdminProjectOverviewResponse;
  events: ReviewEvent[];
  nextActivityCursor: string | null;
  activityExpanded: boolean;
  activityBusy: boolean;
  activityError: string | null;
  onShowAllActivity: () => Promise<void>;
  onCollapseActivity: () => void;
}) {
  const counts = overview.counts;
  const visibleEvents = activityExpanded ? events : events.slice(0, 5);
  const canExpandActivity = events.length > 5 || nextActivityCursor !== null;
  return (
    <section className={`${cardClass} rise-subtle p-6 md:p-8`} style={{ animationDelay: '440ms' }} aria-labelledby="monitoring-title">
      <SectionHeading
        eyebrow={<Eyebrow accent>Monitoring</Eyebrow>}
        title={<span id="monitoring-title">Review progress</span>}
      />
      <dl className="mt-6 grid grid-cols-2 gap-5 sm:grid-cols-5">
        <CountCard label="Use" count={counts.use} tone="success" />
        <CountCard label="Maybe" count={counts.maybe} tone="warning" />
        <CountCard label="Remove" count={counts.remove} tone="error" />
        <CountCard label="Comment" count={counts.comment} tone="plain" />
        <CountCard label="Finished" count={counts.finished} tone="plain" />
      </dl>
      <div className="mt-8">
        <section>
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-faint">Activity</p>
          {events.length === 0 ? (
            <p className="mt-3 text-sm text-muted">No activity has been recorded yet.</p>
          ) : (
            <ol className="mt-3 space-y-3">
              {visibleEvents.map((event) => (
                <li key={event.id} className="border-l-2 border-accent-tint pl-4">
                  <p className="text-sm font-semibold">{eventLabel(event.type)}</p>
                  <p className="mt-1 text-xs text-faint">{formatDateTime(event.created_at)}</p>
                </li>
              ))}
            </ol>
          )}
          {activityError ? <p className="mt-4 text-sm text-error" role="alert">{activityError}</p> : null}
          {canExpandActivity ? (
            <button
              type="button"
              className={`${quietButtonClass} mt-5`}
              onClick={() => activityExpanded ? onCollapseActivity() : void onShowAllActivity()}
              disabled={activityBusy}
            >
              {activityBusy ? <><span className="inline-spinner" aria-hidden="true" />Loading activity</> : activityExpanded ? 'Show recent activity' : 'Show all activity'}
            </button>
          ) : null}
        </section>
      </div>
      <AdminProjectMonitoring overview={overview} />
    </section>
  );
}

function CountCard({ label, count, tone }: { label: string; count: number; tone: 'success' | 'warning' | 'error' | 'plain' }) {
  const toneClass = tone === 'success' ? 'text-success' : tone === 'warning' ? 'text-warning' : tone === 'error' ? 'text-error' : 'text-fg';
  return (
    <div>
      <dt className="mono-label">{label}</dt>
      <dd className={`font-display text-4xl font-semibold leading-none ${toneClass}`}>{count}</dd>
    </div>
  );
}

function DangerSection({
  projectId,
  title,
  completed,
  onCompleted,
  onDeleted,
}: {
  projectId: string;
  title: string;
  completed: boolean;
  onCompleted: () => Promise<void>;
  onDeleted: () => void;
}) {
  const [action, setAction] = useState<'complete' | 'trash' | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    if (!action || (action === 'complete' && confirmation !== title)) return;
    setBusy(true);
    setError(null);
    try {
      if (action === 'complete') {
        await adminApi.completeProject(projectId);
        await onCompleted();
        setAction(null);
        setConfirmation('');
      } else {
        await adminApi.deleteProject(projectId);
        onDeleted();
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not complete that action. Mind trying again?');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rise-subtle rounded-card border border-error/30 bg-error-tint p-6 md:p-8" style={{ animationDelay: '560ms' }} aria-labelledby="danger-title">
      <SectionHeading
        eyebrow={<Eyebrow tone="error">Danger zone</Eyebrow>}
        title={<span id="danger-title">Lifecycle actions</span>}
        description="Completion keeps records and schedules later media cleanup. Deletion moves the project to the Trash tab for 30 days."
      />
      <div className="mt-6 flex flex-wrap gap-3">
        {!completed ? <button type="button" className={secondaryButtonClass} onClick={() => { setAction('complete'); setConfirmation(''); }}>Complete project</button> : null}
        <button type="button" className={`${secondaryButtonClass} border-error/50 text-error hover:border-error hover:bg-card`} onClick={() => { setAction('trash'); setConfirmation(''); }}>Move to trash</button>
      </div>
      {action ? (
        <div className="mt-5 max-w-xl rounded-input border border-error/30 bg-card p-5">
          <h3 className="text-2xl">{action === 'complete' ? 'Complete this project?' : 'Move to trash?'}</h3>
          {action === 'complete' ? (
            <>
              <p className="mt-2 text-sm text-muted">Type <strong className="text-fg">{title}</strong> to confirm.</p>
              <label className="mt-4 block"><FieldLabel>Project name</FieldLabel><input className={inputClass} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
            </>
          ) : (
            <p className="mt-2 text-sm text-muted">
              Move {title} to the trash? Its review links stop working straight away. You can restore it from the Trash tab any time in the next 30 days.
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className={quietButtonClass} onClick={() => setAction(null)}>Cancel</button>
            <button type="button" className={secondaryButtonClass} onClick={() => void confirm()} disabled={busy || (action === 'complete' && confirmation !== title)}>{busy ? <><span className="inline-spinner" aria-hidden="true" />Working</> : action === 'complete' ? 'Confirm completion' : 'Move to trash'}</button>
          </div>
        </div>
      ) : null}
      {error ? <p className="mt-4 rounded-input bg-card p-3 text-sm text-error">{error}</p> : null}
    </section>
  );
}

function eventLabel(type: string): string {
  const labels: Record<string, string> = {
    project_created: 'Project created',
    project_updated: 'Project details updated',
    project_deleted: 'Project deleted',
    project_extended: 'Project expiry extended',
    project_completed: 'Project completed',
    project_published: 'Project published',
    asset_created: 'Asset added',
    asset_updated: 'Asset updated',
    asset_deleted: 'Asset deleted',
    media_deleted: 'Video deleted',
    assets_reordered: 'Assets reordered',
    share_link_created: 'Share link created',
    share_link_reissued: 'Share link reissued',
    share_link_revoked: 'Share link revoked',
    reviewer_finished: 'Reviewer finished',
  };
  return labels[type] ?? type.replace(/_/g, ' ');
}
